import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet.markercluster";
import { Layers, Maximize2, Box, Navigation } from "lucide-react";
import { FleetMap3D } from "@/components/map/FleetMap3D";
import { useLeafletMap } from "@/hooks/useLeafletMap";
import { clusterIcon, vehicleIcon } from "@/components/map/markers";
import { vehicleStatus } from "@/lib/fleet";
import { cn } from "@/lib/utils";

export function FleetMap({ vehicles, selectedId, onSelect, cluster = true, onToggleCluster, follow = false, className, testId = "fleet-map", onMap }) {
  const [mode3d, setMode3d] = useState(false);
  const ref = useRef(null);
  const map = useLeafletMap(ref);

  useEffect(() => {
    if (mode3d || !map) return undefined;

    const timer = setTimeout(() => {
      map.invalidateSize({ animate: false, pan: false });
      map.eachLayer((layer) => {
        if (layer instanceof L.TileLayer) {
          layer.redraw();
        }
      });
    }, 400);

    return () => clearTimeout(timer);
  }, [mode3d, map]);

  const groupRef = useRef(null);
  const markers = useRef({});
  const fitted = useRef(false);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const vehiclePopup = (v) => `
    <div style="min-width:190px">
      <div style="font-weight:700;font-size:14px;margin-bottom:6px">
        ${v.name || "Kendaraan"}
      </div>
      <div style="font-family:monospace;margin-bottom:6px">
        ${v.plate || "-"}
      </div>
      <div>Kecepatan: <b>${Math.round(v.speed || 0)} km/j</b></div>
      <div>ACC: <b>${v.acc === true ? "ON" : v.acc === false ? "OFF" : "BELUM ADA DATA"}</b></div>
      <div>Status: <b>${vehicleStatus(v)}</b></div>
    </div>
  `;

  useEffect(() => {
    if (map && onMap) onMap(map);
  }, [map, onMap]);

  useEffect(() => {
    if (!map) return undefined;
    const g = cluster
      ? L.markerClusterGroup({ maxClusterRadius: 45, disableClusteringAtZoom: 15, showCoverageOnHover: false, iconCreateFunction: clusterIcon })
      : L.layerGroup();
    g.addTo(map);
    groupRef.current = g;
    markers.current = {};
    return () => {
      g.remove();
      groupRef.current = null;
      markers.current = {};
    };
  }, [map, cluster]);

  useEffect(() => {
    const g = groupRef.current;
    if (!g || !map) return;
    const seen = new Set();
    vehicles.forEach((v) => {
      if (v.lat == null) return;
      seen.add(v.id);
      const sel = v.id === selectedId;
      const key = `${vehicleStatus(v)}|${v.direction}|${sel}|${v.plate}`;
      let m = markers.current[v.id];
      if (!m) {
        m = L.marker([v.lat, v.lng], { icon: vehicleIcon(v, sel), zIndexOffset: sel ? 1000 : 0 });
        m.bindPopup(vehiclePopup(v), { maxWidth: 260 });
        m.on("click", () => onSelectRef.current?.(v.id));
        m._gk = key;
        g.addLayer(m);
        markers.current[v.id] = m;
        return;
      }
      const ll = m.getLatLng();
      if (ll.lat !== v.lat || ll.lng !== v.lng) {
        if (cluster) {
          g.removeLayer(m);
          m.setLatLng([v.lat, v.lng]);
          g.addLayer(m);
        } else m.setLatLng([v.lat, v.lng]);
      }
      if (m._gk !== key) {
        m.setIcon(vehicleIcon(v, sel));
        m.setZIndexOffset(sel ? 1000 : 0);
        m.setPopupContent(vehiclePopup(v));
        m._gk = key;
      }
    });
    Object.keys(markers.current).forEach((id) => {
      if (!seen.has(id)) {
        g.removeLayer(markers.current[id]);
        delete markers.current[id];
      }
    });
    if (!fitted.current && seen.size) {
      const pts = vehicles.filter((v) => v.lat != null).map((v) => [v.lat, v.lng]);
      map.fitBounds(L.latLngBounds(pts).pad(0.12));
      fitted.current = true;
    }
  }, [vehicles, selectedId, map, cluster]);

  const sel = vehicles.find((v) => v.id === selectedId);
  useEffect(() => {
    if (map && sel?.lat != null) map.flyTo([sel.lat, sel.lng], Math.max(map.getZoom(), 15), { duration: 0.8 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, map]);

  useEffect(() => {
    if (follow && map && sel?.lat != null) map.panTo([sel.lat, sel.lng], { animate: true, duration: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [follow, sel?.lat, sel?.lng]);

  const fitAll = () => {
    const pts = vehicles.filter((v) => v.lat != null).map((v) => [v.lat, v.lng]);
    if (map && pts.length) map.fitBounds(L.latLngBounds(pts).pad(0.12));
  };

  return (
    <div className={cn("relative overflow-hidden", className)} data-testid={testId}>
      <div
        ref={ref}
        className={`leaflet-container absolute inset-0 z-0 ${mode3d ? "invisible pointer-events-none" : ""}`}
        aria-hidden={mode3d}
      />
      {mode3d && (
        <FleetMap3D
          vehicles={vehicles}
          selectedId={selectedId}
          onSelect={(id) => onSelectRef.current?.(id)}
          follow={follow}
        />
      )}
      <div className="absolute right-3 top-3 z-[500] flex flex-col gap-2">
        <button
          onClick={() => {
            if (sel?.lat == null || sel?.lng == null) return;
            const url = `https://www.google.com/maps/dir/?api=1&destination=${sel.lat},${sel.lng}&travelmode=driving`;
            window.open(url, "_blank", "noopener,noreferrer");
          }}
          disabled={sel?.lat == null || sel?.lng == null}
          data-testid={`${testId}-navigate-btn`}
          title="Navigasi ke mobil dengan Google Maps"
          aria-label="Navigasi ke mobil dengan Google Maps"
          className="flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-card/90 text-foreground backdrop-blur transition-colors hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Navigation className="h-4 w-4" />
        </button>
        <button onClick={fitAll} data-testid={`${testId}-fit-btn`} title="Tampilkan semua"
          className="flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-card/90 text-foreground backdrop-blur transition-colors hover:text-primary">
          <Maximize2 className="h-4 w-4" />
        </button>
        {!mode3d && onToggleCluster && (
          <button onClick={onToggleCluster} data-testid={`${testId}-cluster-btn`} title="Cluster kendaraan"
            className={cn("flex h-9 w-9 items-center justify-center rounded-xl border bg-card/90 backdrop-blur transition-colors",
              cluster ? "border-primary/60 text-primary" : "border-border text-foreground")}>
            <Layers className="h-4 w-4" />
          </button>
        )}
        <button
          onClick={() => setMode3d((v) => !v)}
          data-testid={`${testId}-3d-btn`}
          title={mode3d ? "Kembali ke peta 2D" : "Aktifkan peta 3D"}
          aria-pressed={mode3d}
          className={cn(
            "flex h-9 items-center justify-center gap-1 rounded-xl border px-2 bg-card/90 text-xs font-bold backdrop-blur transition-colors",
            mode3d ? "border-primary/60 text-primary" : "border-border text-foreground"
          )}
        >
          <Box className="h-4 w-4" /> {mode3d ? "3D" : "2D / 3D"}
        </button>
      </div>
    </div>
  );
}
